import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonVariant } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { TextVariant } from 'src/components/atoms/Text';
import { ModalFooter, ModalHeader } from 'src/components/DUP/molecules/ManualUploadTile/styles';
import {
  isAdditionalDocumentProofType,
  isIncomeDocumentProofType
} from 'src/components/DUP/molecules/WizardSubmit';
import Modal, { ModalVariant, ModalWidth } from 'src/components/organisms/Modal';
import { FeatureFlagKeys, useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FocusedIcon, ModalButton, StyledText } from 'src/pages/DUPPage/styles';
import {
  Proof,
  SessionApplication,
  SessionProperty,
  UnauthenticateSessionProofType
} from 'src/types/api';

export interface RequiredDocsModalProps {
  title?: string;
  description?: string;
  handleNext: () => void;
  closeModal: () => void;
}

export interface UseRequiredDocsModalProps {
  proofs?: Proof[];
  property?: SessionProperty;
  application?: SessionApplication;
}

export const REQUIRED_DOCS_TRANSLATION_PREFIX = 'dup.wizard.requiredDocs.';

export const useRequiredDocsModal = ({
  proofs,
  property,
  application
}: UseRequiredDocsModalProps) => {
  const paystubsRequired = getRequiredDocAmt(UnauthenticateSessionProofType.Paystub, property);
  const bankStatementsRequired = getRequiredDocAmt(
    UnauthenticateSessionProofType.BankStatement,
    property
  );

  const [requiredDocsModalInfo, setDocsModalInfo] =
    useState<Pick<RequiredDocsModalProps, 'title' | 'description'>>();

  const closeModal = () => {
    setDocsModalInfo(undefined);
  };

  const {
    onGetFeatureFlagValue: onGetDisclaimerModalFF,
    loading: loadingDisclaimerModalFF,
    data: isDisclaimerModalEnabled
  } = useFeatureFlag({
    projectName: 'fraud-platform',
    featureFlagKey: FeatureFlagKeys.disclaimerModal
  });

  useEffect(() => {
    onGetDisclaimerModalFF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNext = ({
    stepIdx,
    hasPayrollAccountsConnected,
    hasBankAccountsConnected
  }: {
    stepIdx: number;
    hasPayrollAccountsConnected: boolean;
    hasBankAccountsConnected: boolean;
  }) => {
    if (!application || !property || !proofs) return true;

    if (!application.hasPreviouslySubmitted && !requiredDocsModalInfo) {
      if (stepIdx === 1 && paystubsRequired) {
        const incomeDocuments = proofs.filter(isIncomeDocumentProofType);
        if (
          !hasPayrollAccountsConnected &&
          !hasBankAccountsConnected &&
          !incomeDocuments.length &&
          !loadingDisclaimerModalFF &&
          isDisclaimerModalEnabled
        ) {
          setDocsModalInfo({
            title: t(`${REQUIRED_DOCS_TRANSLATION_PREFIX}continuingWithout.paystubs`),
            description: t(`${REQUIRED_DOCS_TRANSLATION_PREFIX}paystubRequirement`, {
              count: paystubsRequired,
              propertyName: property.name
            })
          });
          return false;
        }
      } else if (stepIdx === 2 && bankStatementsRequired) {
        const additionalDocuments = proofs.filter(isAdditionalDocumentProofType);
        if (
          !hasBankAccountsConnected &&
          !additionalDocuments.length &&
          !loadingDisclaimerModalFF &&
          isDisclaimerModalEnabled
        ) {
          setDocsModalInfo({
            title: t(`${REQUIRED_DOCS_TRANSLATION_PREFIX}continuingWithout.additionalDocs`),
            description: t(`${REQUIRED_DOCS_TRANSLATION_PREFIX}bankStatementRequirement`, {
              count: bankStatementsRequired,
              propertyName: property.name
            })
          });
          return false;
        }
      }
    }

    if (requiredDocsModalInfo) closeModal();
    return true;
  };

  return {
    onNext,
    RequiredDocsModal: ({ handleNext }: Pick<RequiredDocsModalProps, 'handleNext'>) => (
      <RequiredDocsModal
        {...requiredDocsModalInfo}
        handleNext={handleNext}
        closeModal={closeModal}
      />
    )
  };
};

const RequiredDocsModal = ({
  title,
  description,
  handleNext,
  closeModal
}: RequiredDocsModalProps) => {
  const { t } = useTranslation();

  if (!title || !description) {
    return null;
  }

  return (
    <Modal
      variant={ModalVariant.wizard}
      width={ModalWidth.small}
      title={
        <ModalHeader>
          <FocusedIcon>
            <Icon icon="file-red" />
          </FocusedIcon>
          <StyledText variant={TextVariant.normal} isBold isStretched>
            {title}
          </StyledText>
        </ModalHeader>
      }
      blurBackground
      showModal
      onCancel={closeModal}
      showModalActions={false}
      isBackClosable={false}
      isEscapeClosable={false}
      showCloseButton
    >
      <Trans>{description}</Trans>
      <ModalFooter>
        <ModalButton name="go back" onClick={() => closeModal()} variant={ButtonVariant.outline}>
          {t('buttonLabel.goBack')}
        </ModalButton>
        <ModalButton
          name="continue"
          onClick={() => {
            handleNext();
          }}
          variant={ButtonVariant.outline}
        >
          {t('buttonLabel.yesContinue')}
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};

export function getRequiredDocAmt(
  proofType: UnauthenticateSessionProofType,
  property?: SessionProperty
) {
  return property?.supportedDoctypes?.[proofType.toLowerCase()] || 0;
}
